import { useCallback, useEffect, useRef, useState } from "react";
import { FolderPlus, Download, Trash2, Upload, Share2, FileText, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useDownload, FORMATS, libError, retryToast } from "@/lib/libraryApi";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const useOrgs = () => {
  const [orgs, setOrgs] = useState([]);
  useEffect(() => { api.get("/teacher/overview").then((r) => setOrgs(r.data.organizations || [])).catch(() => {}); }, []);
  return orgs;
};

export function CollectionsTab({ collections, reload }) {
  const { t } = useI18n();
  const download = useDownload();
  const orgs = useOrgs();
  const [form, setForm] = useState({ name: "", scope: "personal", org_id: "" });
  const [busy, setBusy] = useState(false);
  const admin = orgs.filter((o) => o.is_school_admin);
  // No auto-retry on create (it would add a duplicate); the name stays in the field so the teacher can resend.
  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/library/collections", { ...form, org_id: form.scope === "school" ? form.org_id || admin[0]?.org_id : null }); setForm({ name: "", scope: "personal", org_id: "" }); reload(); }
    catch (er) { toast.error(!er?.response ? t("lib.networkCheck") : libError(er, t, t("lib.collectionFailed"))); }
    finally { setBusy(false); }
  };
  const del = async (cid) => {
    try { await api.delete(`/library/collections/${cid}`); reload(); }
    catch (e) { retryToast(libError(e, t), t, () => del(cid)); }
  };
  const removeItem = async (cid, it) => {
    try { await api.post(`/library/collections/${cid}/items`, { kind: it.kind, ref_id: it.ref_id, remove: true }); reload(); }
    catch (e) { retryToast(libError(e, t, t("lib.collectionFailed")), t, () => removeItem(cid, it)); }
  };
  return (
    <div className="mt-6 space-y-4" data-testid="lib-collections">
      <form onSubmit={create} className="nb-card p-4 flex flex-wrap gap-2 items-end">
        <input className="nb-input flex-1 min-w-[12rem]" dir="auto" maxLength={80} placeholder={t("lib.colPh")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lib-col-name" />
        {admin.length > 0 && (
          <select className="nb-input w-auto" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} data-testid="lib-col-scope">
            <option value="personal">{t("lib.personal")}</option><option value="school">{t("lib.school")}</option>
          </select>
        )}
        <button disabled={busy} className="nb-btn-primary py-2 px-4" data-testid="lib-col-create"><FolderPlus className="w-4 h-4" /> {t("lib.colCreate")}</button>
      </form>
      {collections.length === 0 && <p className="text-sm text-gray-500" data-testid="lib-col-empty">{t("lib.colEmpty")}</p>}
      {collections.map((c) => (
        <div key={c.collection_id} className="nb-card p-4 space-y-2" data-testid={`lib-col-${c.collection_id}`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-bold" dir="auto">{c.name}</div>
              <div className="text-xs text-gray-600" data-testid={`lib-col-count-${c.collection_id}`}>{c.scope === "school" ? t("lib.school") : t("lib.personal")} · {t("lib.items", { count: c.items.length })}</div>
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {FORMATS.map((f) => <button key={f} onClick={() => download(`/library/collections/${c.collection_id}/export?format=${f}`, `${c.name}.${f}`)} className="nb-btn-outline py-1 px-2 text-xs uppercase" data-testid={`lib-col-export-${f}-${c.collection_id}`}><Download className="w-3 h-3" /> {f}</button>)}
              <button onClick={() => del(c.collection_id)} className="nb-btn-outline py-1 px-2 text-xs text-coral" aria-label={t("lib.delete")} data-testid={`lib-col-delete-${c.collection_id}`}><Trash2 className="w-3 h-3" /></button>
            </div>
          </div>
          {c.items.length > 0 && (
            <ul className="flex flex-wrap gap-1.5" data-testid={`lib-col-items-${c.collection_id}`}>
              {c.items.map((it) => (
                <li key={`${it.kind}-${it.ref_id}`} className="nb-tag bg-white text-xs normal-case tracking-normal flex items-center gap-1">
                  <bdi>{it.label}</bdi>
                  <button onClick={() => removeItem(c.collection_id, it)} aria-label={t("lib.removeItem")} data-testid={`lib-col-remove-${c.collection_id}-${it.ref_id}`}><X className="w-3 h-3" /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

export function FilesTab({ collections = [], reload }) {
  const { t } = useI18n();
  const download = useDownload();
  const orgs = useOrgs();
  const [files, setFiles] = useState([]);
  const [loadErr, setLoadErr] = useState(null);
  const [upErr, setUpErr] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const load = useCallback(() => { setLoadErr(null); api.get("/library/resources").then((r) => setFiles(r.data.resources)).catch((e) => setLoadErr(libError(e, t))); }, [t]);
  useEffect(() => { load(); }, [load]);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setUpErr(null);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/library/resources", fd); toast.success(t("lib.uploaded")); load(); }
    catch (e) {
      // Retry only when the server never stored the file (network loss or server error); a 4xx needs a different file.
      const transient = !e?.response || e.response.status >= 500;
      setUpErr({ msg: libError(e, t, t("lib.uploadFailed")), file: transient ? file : null });
    }
    finally { setBusy(false); if (input.current) input.current.value = ""; }
  };
  const share = async (f) => {
    try { await api.post(`/library/resources/${f.resource_id}/share`, { org_id: f.scope === "school" ? null : orgs[0]?.org_id }); load(); }
    catch (e) { retryToast(libError(e, t), t, () => share(f)); }
  };
  const del = async (f) => {
    try { await api.delete(`/library/resources/${f.resource_id}`); toast.success(t("lib.removed")); load(); reload?.(); }
    catch (e) { retryToast(libError(e, t, t("lib.deleteFailed")), t, () => del(f)); }
  };
  const addTo = async (cid, f) => {
    try { await api.post(`/library/collections/${cid}/items`, { kind: "resource", ref_id: f.resource_id }); toast.success(t("lib.added")); reload?.(); }
    catch (er) { retryToast(libError(er, t, t("lib.collectionFailed")), t, () => addTo(cid, f)); }
  };
  return (
    <div className="mt-6 space-y-3" data-testid="lib-files">
      <div className="nb-card p-4 flex items-center gap-3 flex-wrap">
        <input ref={input} type="file" className="hidden" accept=".pdf,.docx,.pptx,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.webp,.mp3,.m4a,.wav,.mp4" onChange={(e) => upload(e.target.files?.[0])} data-testid="lib-file-input" />
        <button onClick={() => input.current?.click()} disabled={busy} className="nb-btn-primary py-2 px-4" data-testid="lib-upload-btn"><Upload className="w-4 h-4" /> {busy ? t("lib.uploading") : t("lib.upload")}</button>
        <p className="text-xs text-gray-600">{t("lib.uploadHint")}</p>
      </div>
      {upErr && (
        <div className="nb-card bg-peach p-3 text-sm flex items-center gap-3 flex-wrap" role="alert" data-testid="lib-upload-error">
          <span>{upErr.msg}</span>
          {upErr.file && <button onClick={() => upload(upErr.file)} className="underline font-bold" data-testid="lib-upload-retry">{t("lib.retry")}</button>}
        </div>
      )}
      {loadErr && (
        <div className="nb-card bg-peach p-3 text-sm flex items-center gap-3" role="alert" data-testid="lib-files-error">
          <span>{loadErr}</span><button onClick={load} className="underline font-bold" data-testid="lib-files-retry">{t("lib.retry")}</button>
        </div>
      )}
      {!loadErr && files.length === 0 && <p className="text-sm text-gray-500" data-testid="lib-files-empty">{t("lib.filesEmpty")}</p>}
      {files.map((f) => (
        <div key={f.resource_id} className="nb-card p-3 flex items-center justify-between gap-3 flex-wrap min-w-0 max-w-full" data-testid={`lib-file-${f.resource_id}`}>
          <div className="flex items-center gap-2 min-w-0"><FileText className="w-4 h-4 shrink-0" /><span className="truncate" dir="auto">{f.filename}</span>
            <span className="text-xs text-gray-500">{Math.ceil(f.size / 1024)} KB · {f.scope === "school" ? t("lib.school") : t("lib.personal")}
              {f.used_in_assignments > 0 && <> · <span data-testid={`lib-file-used-${f.resource_id}`}>{t("lib.usedIn", { count: f.used_in_assignments })}</span></>}</span></div>
          <div className="flex gap-1.5 flex-wrap min-w-0">
            {collections.length > 0 && (
              <select className="nb-input py-1 px-2 text-xs w-auto" value="" aria-label={t("lib.addTo")} data-testid={`lib-file-collection-${f.resource_id}`}
                onChange={(e) => e.target.value && addTo(e.target.value, f)}>
                <option value="">{t("lib.addTo")}</option>
                {collections.map((c) => <option key={c.collection_id} value={c.collection_id}>{c.name}</option>)}
              </select>
            )}
            <button onClick={() => download(`/library/resources/${f.resource_id}/download`, f.filename)} className="nb-btn-outline py-1 px-2 text-xs" aria-label={t("lib.download")} data-testid={`lib-file-download-${f.resource_id}`}><Download className="w-3 h-3" /></button>
            {orgs.length > 0 && <button onClick={() => share(f)} className="nb-btn-outline py-1 px-2 text-xs" data-testid={`lib-file-share-${f.resource_id}`}><Share2 className="w-3 h-3" /> {f.scope === "school" ? t("lib.unshare") : t("lib.share")}</button>}
            <button onClick={() => setConfirm(f)} className="nb-btn-outline py-1 px-2 text-xs text-coral" aria-label={t("lib.delete")} data-testid={`lib-file-delete-${f.resource_id}`}><Trash2 className="w-3 h-3" /></button>
          </div>
        </div>
      ))}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="lib-file-delete-dialog">
          <AlertDialogHeader className="text-start sm:text-start">
            <AlertDialogTitle>{t("lib.removeTitle")}</AlertDialogTitle>
            <AlertDialogDescription data-testid="lib-file-delete-msg">{confirm?.used_in_assignments > 0 ? t("lib.removeUsed") : t("lib.removeUnused")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="lib-file-delete-cancel">{t("lib.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => { del(confirm); setConfirm(null); }} data-testid="lib-file-delete-confirm">{t("lib.removeYes")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
